import type { KindKey } from '../data/types';

/** 工種の役割。3D 表示で、どの構造物をどこまで描くかの根拠になる。 */
export type Role = '' | 'found' | 'frame' | 'finish' | 'wall' | 'pond' | 'road' | 'green';

/** 建物。1 階ずつ箱で描く。`pitK` は根切り深さにかける係数。 */
export interface BuildingBlock {
  kind: 'bldg';
  x: number;
  z: number;
  w: number;
  d: number;
  /** 階数（地下を含む）と 1 階の高さ。 */
  n: number;
  uh: number;
  basement?: boolean;
  pitK: number;
}
/** 区間に分けて作る構造物（擁壁）。 */
export interface SegmentBlock {
  kind: 'seg';
  axis: 'x';
  x: number;
  z: number;
  w: number;
  d: number;
  y0: number;
  h: number;
  n: number;
  role: Role;
  col: string;
}
/** 場内道路。 */
export interface RoadBlock {
  kind: 'road';
  x0: number;
  x1: number;
  z: number;
  w: number;
  n: number;
  layer: 'pave';
  role: Role;
}
/** 調整池。 */
export interface WaterBlock {
  kind: 'water';
  x: number;
  z: number;
  w: number;
  d: number;
  role: Role;
  pond: true;
}
export type Block = BuildingBlock | SegmentBlock | RoadBlock | WaterBlock;

/** 工種のテンプレート。`pred` は先行工種の添字（省略時は 1 つ前）。 */
export interface TaskTemplate {
  name: string;
  /** 着工日からの日数。 */
  offset: number;
  /** 期間（日）。 */
  dur: number;
  /** 全体進捗に占める重み（合計 1）。 */
  weight: number;
  role: Role;
  /** 土工か。 */
  earth: boolean;
  /** 工法の説明。 */
  method: string;
  pred?: number;
}

export interface Kind {
  type: '建築' | '土木';
  label: string;
  scale: string;
  /** 根切り深さ（工業団地造成は調整池深さ）の初期値（m）。 */
  pit: number;
  pitLabel: string;
  crane: boolean;
  /** 外周のすり付け幅（m）。 */
  mD: number;
  color: string;
  blocks: (w: number, d: number) => Block[];
  tasks: readonly TaskTemplate[];
}

type TaskRow = [
  name: string,
  offset: number,
  dur: number,
  weight: number,
  role: Role | 'earth',
  method: string,
  pred?: number,
];
const tasks = (rows: TaskRow[]): TaskTemplate[] =>
  rows.map(([name, offset, dur, weight, role, method, pred]) => ({
    name,
    offset,
    dur,
    weight,
    role: role === 'earth' ? '' : role,
    earth: role === 'earth',
    method,
    ...(pred !== undefined ? { pred } : {}),
  }));

export const KINDS: Readonly<Record<KindKey, Kind>> = {
  office: {
    type: '建築',
    label: 'オフィスビル',
    scale: 'S造 地下1階・地上12階',
    pit: 5.5,
    pitLabel: '根切り深さ',
    crane: true,
    mD: 5,
    color: '#3b82f6',
    blocks: (w) => [
      { kind: 'bldg', x: -w * 0.11, z: 0, w: 38, d: 30, n: 13, uh: 4.2, basement: true, pitK: 1 },
      { kind: 'bldg', x: w * 0.31, z: 4, w: 22, d: 22, n: 3, uh: 4.5, pitK: 0.3 },
    ],
    tasks: tasks([
      ['準備工', 0, 30, 0.03, '', '仮囲い・ゲート設置、山留め親杭・横矢板'],
      ['山留・根切り（土工）', 30, 60, 0.07, 'earth', 'バックホウで根切り、残土は場外搬出'],
      ['杭・基礎', 95, 90, 0.12, 'found', '場所打ち杭、基礎躯体・耐圧盤'],
      ['鉄骨・躯体', 185, 180, 0.3, 'frame', 'タワークレーンで鉄骨建方、床コン打設'],
      ['外装', 305, 150, 0.15, 'finish', 'カーテンウォール取付（下層から）', 3],
      ['内装・設備', 365, 150, 0.25, '', '空調・電気幹線、内装仕上げ', 4],
      ['外構・検査', 485, 60, 0.08, '', '外構、消防・完了検査'],
    ]),
  },
  apt: {
    type: '建築',
    label: '共同住宅',
    scale: 'RC造 地上15階',
    pit: 1.6,
    pitLabel: '根切り深さ',
    crane: true,
    mD: 5,
    color: '#8b5cf6',
    blocks: () => [{ kind: 'bldg', x: 0, z: 5, w: 54, d: 16, n: 15, uh: 3.1, pitK: 1 }],
    tasks: tasks([
      ['準備工', 0, 30, 0.02, '', '仮囲い・近隣家屋調査'],
      ['杭工事', 30, 45, 0.08, '', '既製杭（中掘り工法）'],
      ['根切り（土工）', 75, 55, 0.05, 'earth', '根切り・床付け、残土は場外搬出'],
      ['基礎', 130, 60, 0.1, 'found', '基礎梁・耐圧盤'],
      ['躯体', 195, 330, 0.4, 'frame', 'タワークレーン＋システム型枠'],
      ['外装', 405, 240, 0.12, 'finish', '外壁タイル・サッシ（下層から）', 4],
      ['内装・設備', 435, 240, 0.18, '', '住戸内装・設備', 5],
      ['外構・検査', 675, 50, 0.05, '', '外構・植栽、完了検査'],
    ]),
  },
  hosp: {
    type: '建築',
    label: '病院',
    scale: 'RC造 地下1階・地上7階',
    pit: 4.5,
    pitLabel: '根切り深さ',
    crane: true,
    mD: 5,
    color: '#14b8a6',
    blocks: (w) => [
      { kind: 'bldg', x: -w * 0.12, z: -4, w: 64, d: 36, n: 8, uh: 4.2, basement: true, pitK: 1 },
      { kind: 'bldg', x: w * 0.3, z: 6, w: 24, d: 30, n: 3, uh: 4.5, pitK: 0.3 },
    ],
    tasks: tasks([
      ['準備工', 0, 30, 0.03, '', '仮囲い・仮設、近隣説明'],
      ['山留・根切り（土工）', 30, 75, 0.07, 'earth', 'SMW山留め、根切り・残土搬出'],
      ['杭・基礎', 105, 100, 0.12, 'found', '場所打ち杭、免震基礎'],
      ['躯体', 205, 300, 0.3, 'frame', 'タワークレーンで躯体（1フロア約1か月）'],
      ['外装', 400, 180, 0.12, 'finish', 'PCa外壁・サッシ', 3],
      ['設備・内装', 430, 300, 0.28, '', '医療ガス・空調・内装', 4],
      ['外構・検査', 730, 60, 0.08, '', '外構・駐車場、完了検査'],
    ]),
  },
  school: {
    type: '建築',
    label: '学校校舎',
    scale: 'RC造 地上4階＋屋内運動場',
    pit: 1.2,
    pitLabel: '根切り深さ',
    crane: false,
    mD: 5,
    color: '#e11d48',
    blocks: () => [
      { kind: 'bldg', x: -14, z: -22, w: 70, d: 16, n: 4, uh: 3.8, pitK: 1 },
      { kind: 'bldg', x: 34, z: 6, w: 16, d: 40, n: 4, uh: 3.8, pitK: 1 },
      { kind: 'bldg', x: -26, z: 18, w: 36, d: 28, n: 1, uh: 11, pitK: 1 },
    ],
    tasks: tasks([
      ['準備・解体', 0, 60, 0.05, '', '仮設校舎、既存校舎解体'],
      ['土工（根切り・整地）', 60, 45, 0.05, 'earth', '根切り・整地、残土搬出'],
      ['杭・基礎', 105, 75, 0.12, 'found', '既製杭・基礎'],
      ['躯体', 180, 180, 0.3, 'frame', '移動式クレーン・在来型枠'],
      ['外装', 300, 150, 0.15, 'finish', '外壁・屋上防水', 3],
      ['内装・設備', 330, 180, 0.25, '', '内装・設備', 4],
      ['外構・検査', 510, 60, 0.08, '', '校庭整備、完了検査'],
    ]),
  },
  logi: {
    type: '建築',
    label: '物流センター',
    scale: 'S造 地上4階',
    pit: 0.8,
    pitLabel: '根切り深さ',
    crane: false,
    mD: 8,
    color: '#10b981',
    blocks: () => [{ kind: 'bldg', x: 0, z: -6, w: 136, d: 84, n: 4, uh: 7.5, pitK: 1 }],
    tasks: tasks([
      ['準備工', 0, 30, 0.03, '', '伐採・仮設事務所'],
      ['敷地造成（土工）', 30, 90, 0.08, 'earth', '場内流用を基本に切盛、層厚30cm締固め'],
      ['杭・基礎', 120, 90, 0.12, 'found', '既製杭、独立基礎・地中梁'],
      ['鉄骨・躯体', 215, 150, 0.3, 'frame', '移動式クレーンで鉄骨建方'],
      ['外装', 330, 150, 0.15, 'finish', '折板屋根・外壁パネル', 3],
      ['設備・内装', 395, 180, 0.24, '', '垂直搬送機・防災設備', 4],
      ['外構・検査', 545, 60, 0.08, '', 'トラックヤード舗装、完了検査'],
    ]),
  },
  factory: {
    type: '建築',
    label: '工場',
    scale: 'S造 地上2階＋事務棟3階',
    pit: 1.5,
    pitLabel: '根切り深さ',
    crane: false,
    mD: 6,
    color: '#f97316',
    blocks: () => [
      { kind: 'bldg', x: -10, z: -6, w: 100, d: 60, n: 2, uh: 9, pitK: 1 },
      { kind: 'bldg', x: 50, z: 30, w: 30, d: 20, n: 3, uh: 4, pitK: 1 },
    ],
    tasks: tasks([
      ['準備工', 0, 30, 0.03, '', '仮設・測量'],
      ['造成・根切り（土工）', 30, 75, 0.08, 'earth', '整地・根切り、場内流用'],
      ['杭・基礎', 105, 90, 0.14, 'found', '既製杭・機械基礎'],
      ['鉄骨・躯体', 195, 150, 0.28, 'frame', '鉄骨建方・床スラブ'],
      ['外装', 300, 120, 0.12, 'finish', '外壁・屋根', 3],
      ['設備・内装', 330, 210, 0.27, '', 'クリーンルーム・生産設備', 4],
      ['外構・検査', 540, 60, 0.08, '', '外構・完了検査'],
    ]),
  },
  res: {
    type: '土木',
    label: '宅地造成',
    scale: '宅地造成（単一造成高）',
    pit: 0,
    pitLabel: '—',
    crane: false,
    mD: 20,
    color: '#a855f7',
    blocks: (w, d) => [
      {
        kind: 'seg',
        axis: 'x',
        x: 0,
        z: -(d / 2 - 24),
        w: w * 0.55,
        d: 1.2,
        y0: -1.2,
        h: 3.5,
        n: 6,
        role: 'wall',
        col: '#b7bcc4',
      },
      {
        kind: 'road',
        x0: -w * 0.36,
        x1: w * 0.36,
        z: -d * 0.08,
        w: 8,
        n: 8,
        layer: 'pave',
        role: 'road',
      },
      {
        kind: 'road',
        x0: -w * 0.36,
        x1: w * 0.36,
        z: d * 0.2,
        w: 6,
        n: 8,
        layer: 'pave',
        role: 'road',
      },
    ],
    tasks: tasks([
      ['準備・伐採', 0, 45, 0.04, '', '伐採・除根、仮設沈砂池'],
      ['切土・盛土（土工）', 47, 240, 0.4, 'earth', '切土を盛土へ流用、層厚30cm締固め'],
      ['擁壁・法面工', 180, 180, 0.16, 'wall', 'L型擁壁・法面整形', 1],
      ['排水・道路', 330, 150, 0.18, 'road', '雨水排水・区画道路舗装', 2],
      ['宅地整地・緑化', 450, 90, 0.12, 'green', '宅盤整地・法面緑化', 3],
      ['完成検査', 540, 30, 0.1, '', '出来形検査・引渡し', 4],
    ]),
  },
  ind: {
    type: '土木',
    label: '工業団地造成',
    scale: '工業団地造成（単一造成高）・調整池',
    pit: 3,
    pitLabel: '調整池深さ',
    crane: false,
    mD: 20,
    color: '#0ea5e9',
    blocks: (w, d) => [
      { kind: 'water', x: w * 0.33, z: d * 0.26, w: 34, d: 30, role: 'pond', pond: true },
      {
        kind: 'road',
        x0: -w * 0.38,
        x1: w * 0.2,
        z: -d * 0.1,
        w: 10,
        n: 8,
        layer: 'pave',
        role: 'road',
      },
      {
        kind: 'road',
        x0: -w * 0.38,
        x1: w * 0.38,
        z: -d * 0.32,
        w: 8,
        n: 8,
        layer: 'pave',
        role: 'road',
      },
    ],
    tasks: tasks([
      ['準備・伐採', 0, 45, 0.04, '', '伐採・除根、工事用道路'],
      ['切土・盛土（土工）', 47, 260, 0.38, 'earth', '丘陵部を切土し低地へ流用盛土'],
      ['調整池', 240, 150, 0.1, 'pond', '掘込式調整池・放流施設', 1],
      ['排水・場内道路', 330, 150, 0.18, 'road', 'U型側溝・雨水管、場内道路舗装', 1],
      ['法面・緑化', 450, 90, 0.12, 'green', '法面整形・植生基材吹付', 3],
      ['完成検査', 540, 30, 0.18, '', '出来形・品質検査、引渡し', 4],
    ]),
  },
};

/** 現場の色（連番の順にくり返し使う）。 */
export const SITE_COLORS: readonly string[] = [
  '#3b82f6',
  '#10b981',
  '#8b5cf6',
  '#e11d48',
  '#0ea5e9',
  '#f97316',
  '#14b8a6',
  '#a855f7',
  '#eab308',
  '#ec4899',
  '#22c55e',
  '#6366f1',
  '#06b6d4',
  '#f43f5e',
  '#84cc16',
];
